import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { habitsApi } from '../api/client';
import type { HabitDefinition, HabitEntry } from '../api/client';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import './HabitHistory.css';

const HabitHistory: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [habit, setHabit] = useState<HabitDefinition | null>(null);
  const [habitHistory, setHabitHistory] = useState<HabitEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showLogModal, setShowLogModal] = useState(false);
  const [filterStartDate, setFilterStartDate] = useState<string>('');
  const [filterEndDate, setFilterEndDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [newEntry, setNewEntry] = useState({
    date: new Date().toISOString().split('T')[0],
    completed: false,
    value: '',
    notes: ''
  });

  useEffect(() => {
    fetchHabitHistory();
  }, [id]);

  const fetchHabitHistory = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const [definitionsResponse, entriesResponse] = await Promise.all([
        habitsApi.getHabitDefinitions(),
        habitsApi.getHabitEntries({ habitId: id })
      ]);

      const foundHabit = definitionsResponse.data.find((h: HabitDefinition) => h.id === id);

      if (!foundHabit) {
        setError('Habit not found');
        return;
      }

      setHabit(foundHabit);
      const sortedHistory = entriesResponse.data.sort((a: HabitEntry, b: HabitEntry) =>
        new Date(a.date).getTime() - new Date(b.date).getTime()
      );
      setHabitHistory(sortedHistory);

      // Set default start date to first entry or 30 days ago
      if (sortedHistory.length > 0 && !filterStartDate) {
        const firstEntryDate = new Date(sortedHistory[0].date);
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

        // Use the more recent of: first entry or 30 days ago
        const startDate = firstEntryDate > thirtyDaysAgo ? firstEntryDate : thirtyDaysAgo;
        setFilterStartDate(startDate.toISOString().split('T')[0]);
      }
    } catch (err) {
      console.error('Error fetching habit history:', err);
      setError('Failed to load habit history');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'health': return '❤️';
      case 'productivity': return '⚡';
      case 'personal': return '🌱';
      case 'social': return '👥';
      case 'learning': return '📚';
      default: return '✅';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'health': return '#e74c3c';
      case 'productivity': return '#3498db';
      case 'personal': return '#27ae60';
      case 'social': return '#f39c12';
      case 'learning': return '#9b59b6';
      default: return '#95a5a6';
    }
  };

  const getStreakCount = (habitId: string) => {
    const entries = habitHistory
      .filter(e => e.habitId === habitId && e.completed)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    if (entries.length === 0) return 0;

    let streak = 0;
    let currentDate = new Date();

    for (const entry of entries) {
      const entryDate = new Date(entry.date);
      const diffDays = Math.floor((currentDate.getTime() - entryDate.getTime()) / (1000 * 60 * 60 * 24));

      if (diffDays === streak) {
        streak++;
        currentDate = entryDate;
      } else {
        break;
      }
    }

    return streak;
  };

  const handleLogEntry = async () => {
    if (!habit || !id) return;

    try {
      const entryData = {
        habitId: id,
        date: newEntry.date,
        completed: newEntry.completed,
        value: newEntry.value ? parseFloat(newEntry.value) : undefined,
        unit: habit.unit,
        notes: newEntry.notes || undefined
      };

      await habitsApi.createHabitEntry(entryData);
      await fetchHabitHistory();
      setShowLogModal(false);
      resetNewEntry();
    } catch (err) {
      console.error('Error logging habit entry:', err);
      setError('Failed to log habit entry');
    }
  };

  const resetNewEntry = () => {
    setNewEntry({
      date: new Date().toISOString().split('T')[0],
      completed: false,
      value: '',
      notes: ''
    });
  };

  const generateGraphData = () => {
    if (habitHistory.length === 0 || !filterStartDate || !filterEndDate) return [];

    // Use filter dates for the range
    const minDate = new Date(filterStartDate);
    const maxDate = new Date(filterEndDate);

    // Create a map of existing entries by date
    const entriesByDate = new Map();
    habitHistory.forEach(entry => {
      const dateKey = entry.date;
      entriesByDate.set(dateKey, entry);
    });

    // Generate all dates in the range
    const allDates = [];
    const currentDate = new Date(minDate);
    while (currentDate <= maxDate) {
      const dateKey = currentDate.toISOString().split('T')[0];
      const entry = entriesByDate.get(dateKey);

      if (habit?.targetValue) {
        // For value-based habits
        allDates.push({
          date: currentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          value: entry?.value || 0,
          target: habit.targetValue,
          hasEntry: !!entry
        });
      } else {
        // For completion-based habits
        allDates.push({
          date: currentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          completed: entry?.completed ? 1 : 0,
          hasEntry: !!entry
        });
      }

      currentDate.setDate(currentDate.getDate() + 1);
    }

    return allDates;
  };

  const handleQuickDateRange = (range: 'week' | 'month' | '3months' | 'year' | 'all') => {
    const today = new Date();
    const endDate = today.toISOString().split('T')[0];
    let startDate: string;

    switch (range) {
      case 'week':
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        startDate = weekAgo.toISOString().split('T')[0];
        break;
      case 'month':
        const monthAgo = new Date(today);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        startDate = monthAgo.toISOString().split('T')[0];
        break;
      case '3months':
        const threeMonthsAgo = new Date(today);
        threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
        startDate = threeMonthsAgo.toISOString().split('T')[0];
        break;
      case 'year':
        const yearAgo = new Date(today);
        yearAgo.setFullYear(yearAgo.getFullYear() - 1);
        startDate = yearAgo.toISOString().split('T')[0];
        break;
      case 'all':
        if (habitHistory.length > 0) {
          startDate = habitHistory[0].date;
        } else {
          const monthAgo = new Date(today);
          monthAgo.setMonth(monthAgo.getMonth() - 1);
          startDate = monthAgo.toISOString().split('T')[0];
        }
        break;
    }

    setFilterStartDate(startDate);
    setFilterEndDate(endDate);
  };

  if (loading) {
    return (
      <div className="habit-history-page">
        <div className="loading">Loading habit history...</div>
      </div>
    );
  }

  if (error || !habit) {
    return (
      <div className="habit-history-page">
        <div className="error-container">
          <div className="error">{error || 'Habit not found'}</div>
          <Link to="/habits" className="btn btn-primary">
            ← Back to Habits
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="habit-history-page">
      {/* Header with back button */}
      <div className="history-header">
        <div className="header-top">
          <Link to="/habits" className="back-link">
            <span className="back-arrow">←</span>
            Back to Habits
          </Link>
          <button
            className="btn btn-primary"
            onClick={() => setShowLogModal(true)}
          >
            ➕ Log Entry
          </button>
        </div>
        <div className="habit-title-section">
          <div className="habit-title-content">
            <span className="habit-icon-large">{getCategoryIcon(habit.category)}</span>
            <div className="habit-title-info">
              <h1>{habit.name}</h1>
              <span
                className="habit-category-badge"
                style={{ backgroundColor: getCategoryColor(habit.category) }}
              >
                {habit.category}
              </span>
            </div>
          </div>
          {habit.description && (
            <p className="habit-description-header">{habit.description}</p>
          )}
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="history-stats-grid">
        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <div className="stat-label">Total Entries</div>
            <div className="stat-value">{habitHistory.length}</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <div className="stat-label">Completed</div>
            <div className="stat-value">
              {habitHistory.filter(h => h.completed).length}
            </div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📈</div>
          <div className="stat-content">
            <div className="stat-label">Completion Rate</div>
            <div className="stat-value">
              {habitHistory.length > 0
                ? Math.round((habitHistory.filter(h => h.completed).length / habitHistory.length) * 100)
                : 0}%
            </div>
          </div>
        </div>
        <div className="stat-card highlight">
          <div className="stat-icon">🔥</div>
          <div className="stat-content">
            <div className="stat-label">Current Streak</div>
            <div className="stat-value streak">
              {getStreakCount(habit.id)} days
            </div>
          </div>
        </div>
      </div>

      {/* Graph Section */}
      {habitHistory.length > 0 && (
        <div className="graph-section">
          <div className="graph-header">
            <h2>📈 Progress Over Time</h2>
          </div>

          {/* Date Range Filter */}
          <div className="date-filter-section">
            <div className="date-inputs">
              <div className="date-input-group">
                <label>Start Date</label>
                <input
                  type="date"
                  value={filterStartDate}
                  onChange={(e) => setFilterStartDate(e.target.value)}
                  max={filterEndDate}
                  className="date-filter-input"
                />
              </div>
              <div className="date-input-group">
                <label>End Date</label>
                <input
                  type="date"
                  value={filterEndDate}
                  onChange={(e) => setFilterEndDate(e.target.value)}
                  min={filterStartDate}
                  className="date-filter-input"
                />
              </div>
            </div>
            <div className="quick-filters">
              <button
                className="quick-filter-btn"
                onClick={() => handleQuickDateRange('week')}
              >
                Last 7 Days
              </button>
              <button
                className="quick-filter-btn"
                onClick={() => handleQuickDateRange('month')}
              >
                Last Month
              </button>
              <button
                className="quick-filter-btn"
                onClick={() => handleQuickDateRange('3months')}
              >
                Last 3 Months
              </button>
              <button
                className="quick-filter-btn"
                onClick={() => handleQuickDateRange('year')}
              >
                Last Year
              </button>
              <button
                className="quick-filter-btn"
                onClick={() => handleQuickDateRange('all')}
              >
                All Time
              </button>
            </div>
          </div>

          <div className="graph-container">
            <ResponsiveContainer width="100%" height={400}>
              {habit.targetValue ? (
                <LineChart data={generateGraphData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    interval="preserveStartEnd"
                  />
                  <YAxis />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #e9ecef', borderRadius: '8px' }}
                    labelStyle={{ fontWeight: 'bold', marginBottom: '5px' }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#3498db"
                    strokeWidth={2}
                    name={`Value (${habit.unit})`}
                    dot={{ r: 4 }}
                    connectNulls
                  />
                  <Line
                    type="monotone"
                    dataKey="target"
                    stroke="#e74c3c"
                    strokeDasharray="5 5"
                    strokeWidth={2}
                    name="Target"
                    dot={false}
                  />
                </LineChart>
              ) : (
                <BarChart data={generateGraphData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    interval="preserveStartEnd"
                  />
                  <YAxis />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #e9ecef', borderRadius: '8px' }}
                    labelStyle={{ fontWeight: 'bold', marginBottom: '5px' }}
                  />
                  <Legend />
                  <Bar
                    dataKey="completed"
                    fill="#27ae60"
                    name="Completed"
                  />
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* History List */}
      <div className="history-list-section">
        <h2>📝 Entry History ({habitHistory.length} entries)</h2>
        <div className="history-entries-list">
          {habitHistory.length === 0 ? (
            <div className="no-history">
              <span className="no-history-icon">📭</span>
              <p>No entries yet for this habit.</p>
              <Link to="/habits" className="btn btn-primary">
                Start Tracking
              </Link>
            </div>
          ) : (
            [...habitHistory].reverse().map((entry) => (
              <div key={entry.id} className="history-entry-card">
                <div className="entry-card-header">
                  <div className="entry-date-full">
                    <span className="date-icon">📅</span>
                    <div className="date-text">
                      <div className="date-day">
                        {new Date(entry.date).toLocaleDateString('en-US', {
                          weekday: 'long',
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </div>
                    </div>
                  </div>
                  <div className="entry-status-badge">
                    {entry.completed ? (
                      <span className="status-badge completed">✅ Completed</span>
                    ) : (
                      <span className="status-badge incomplete">⏳ Incomplete</span>
                    )}
                  </div>
                </div>

                {entry.value !== undefined && (
                  <div className="entry-value-section">
                    <div className="value-display">
                      <span className="value-label">Value:</span>
                      <span className="value-number">{entry.value} {entry.unit}</span>
                    </div>
                    {habit.targetValue && (
                      <div className="value-progress-bar">
                        <div className="progress-bar-container">
                          <div
                            className="progress-bar-fill"
                            style={{ width: `${Math.min((entry.value / habit.targetValue) * 100, 100)}%` }}
                          />
                        </div>
                        <span className="progress-percentage">
                          {Math.round((entry.value / habit.targetValue) * 100)}% of target
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {entry.notes && (
                  <div className="entry-notes-section">
                    <span className="notes-icon">📝</span>
                    <span className="notes-text">{entry.notes}</span>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Log Entry Modal */}
      {showLogModal && (
        <div className="modal-overlay" onClick={() => setShowLogModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Log Entry for {habit.name}</h3>
              <button
                className="modal-close"
                onClick={() => setShowLogModal(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Date *</label>
                <input
                  type="date"
                  value={newEntry.date}
                  onChange={(e) => setNewEntry({...newEntry, date: e.target.value})}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={newEntry.completed}
                    onChange={(e) => setNewEntry({...newEntry, completed: e.target.checked})}
                    className="form-checkbox"
                  />
                  <span>Completed</span>
                </label>
              </div>

              {habit.targetValue && (
                <div className="form-group">
                  <label>
                    Value ({habit.unit})
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={newEntry.value}
                    onChange={(e) => setNewEntry({...newEntry, value: e.target.value})}
                    className="form-input"
                    placeholder={`Enter value in ${habit.unit}...`}
                  />
                  {habit.targetValue && (
                    <small className="form-hint">
                      Target: {habit.targetValue} {habit.unit}
                    </small>
                  )}
                </div>
              )}

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={newEntry.notes}
                  onChange={(e) => setNewEntry({...newEntry, notes: e.target.value})}
                  className="form-textarea"
                  rows={3}
                  placeholder="Optional notes about this entry..."
                />
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-success"
                onClick={handleLogEntry}
              >
                ✅ Log Entry
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowLogModal(false);
                  resetNewEntry();
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HabitHistory;
